import React, { useState } from 'react';
import { auth, workoutPlans } from '../../services/api';
import { createPlanBody } from '../../services/planDiff';
import { formatKg } from '../WorkoutPlayer/playerLogic';
import { Modal, errorList } from '../ExerciseLibrary/shared';
import { toStudioDraft } from './PlanGeneratorDialog';
import '../../styles/ui.css';
import '../ExerciseLibrary/ExerciseLibrary.css';

/*
 * «Gerar o meu treino» — o ATLETA propõe o SEU plano.
 *
 * É o mesmo motor determinístico do estúdio de planos, mas sem nada do treinador: o atleta não escolhe outra
 * pessoa (o servidor força o atleta da sessão), não ajusta a prescrição e NADA é gravado até ele carregar em
 * «Usar este plano». Quando grava, é um plano do próprio atleta (MemberPlan) com os dias e a prescrição que o
 * gerador devolveu — no formato exato que o servidor aceita (o mesmo `createPlanBody` do estúdio).
 *
 * Sem histórico, o gerador não devolve carga nenhuma: aqui não se escreve zero nem se inventam valores, e as
 * notas do próprio gerador dizem que não há cargas a propor.
 */

const GOALS = [
  ['hypertrophy', 'Hipertrofia'],
  ['strength', 'Força'],
  ['endurance', 'Resistência'],
  ['general', 'Geral'],
];
const LEVELS = [['beginner', 'Iniciante'], ['intermediate', 'Intermédio'], ['advanced', 'Avançado']];
const DAYS_PER_WEEK = [2, 3, 4, 5, 6];

/** A prescrição como o atleta a lê: "4 × 6–10 · 105s de descanso · 62,5 kg". Carga só quando o gerador a deu. */
export const prescriptionLine = (ex) => {
  const reps = ex.repsMin && ex.repsMax && ex.repsMin !== ex.repsMax
    ? `${ex.repsMin}–${ex.repsMax}`
    : (ex.repsMin ?? ex.repsMax ?? null);
  const parts = [];
  if (ex.sets && reps) parts.push(`${ex.sets} × ${reps}`);
  else if (reps) parts.push(String(reps));
  else if (ex.sets) parts.push(`${ex.sets} séries`);
  if (ex.restSeconds) parts.push(`${ex.restSeconds}s de descanso`);
  if (ex.targetWeightKg != null) parts.push(`${formatKg(ex.targetWeightKg)} kg`);
  return parts.join(' · ');
};

/**
 * O corpo do pedido de gravação, no formato EXATO que o servidor aceita (o mesmo que o estúdio usa):
 * um plano do próprio atleta, com os dias e a prescrição que o gerador devolveu. Sem histórico não há carga,
 * e por isso não vai nenhuma.
 */
export const toMyPlanRequest = (plan, userId) =>
  createPlanBody(toStudioDraft(plan, { userId }), { planType: 'MemberPlan', userId });

export default function MyPlanGeneratorDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ goal: 'hypertrophy', experience: 'beginner', daysPerWeek: 3, sessionMinutes: 60, seed: 0 });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const generate = async (seed = form.seed) => {
    setBusy(true);
    setErrors([]);
    try {
      const { data } = await workoutPlans.generateMine({
        goal: form.goal,
        experience: form.experience,
        daysPerWeek: Number(form.daysPerWeek),
        sessionMinutes: Number(form.sessionMinutes),
        seed,
      });
      setForm((f) => ({ ...f, seed }));
      setPlan(data);
    } catch (e) {
      // Falhou: fica-se nos parâmetros, diz-se o que falhou e o botão «Gerar proposta» continua lá para repetir.
      setErrors(errorList(e, 'Não foi possível gerar a proposta. Tente outra vez.'));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);

    // De quem é o plano: quem o SERVIDOR diz que está autenticado. Falhar aqui é uma falha a sério — não se
    // grava um plano sem dono nem se inventa um atleta.
    let userId;
    try {
      const { data: me } = await auth.me();
      userId = me?.id;
    } catch {
      userId = undefined;
    }
    if (!userId) {
      setErrors(['Não foi possível confirmar a sua sessão. Volte a entrar e tente outra vez.']);
      setSaving(false);
      return;
    }

    try {
      await workoutPlans.create(toMyPlanRequest(plan, userId));
      onSaved?.(plan);
    } catch (e) {
      // A proposta fica à vista; «Usar este plano» continua lá para repetir.
      setErrors(errorList(e, 'Não foi possível guardar o plano. Tente outra vez.'));
    } finally {
      setSaving(false);
    }
  };

  const days = plan?.days || [];
  const total = days.reduce((n, d) => n + (d.exercises?.length || 0), 0);

  return (
    <Modal
      title="Gerar o meu treino"
      onClose={onClose}
      footer={plan ? (
        <>
          <button type="button" className="lib-btn" onClick={() => { setPlan(null); setErrors([]); }} disabled={busy || saving}>← Parâmetros</button>
          <button type="button" className="lib-btn" onClick={() => generate(form.seed + 1)} disabled={busy || saving}>Outra proposta</button>
          <button type="button" className="lib-btn primary" onClick={save} disabled={busy || saving || total === 0} data-testid="myplan-use">
            {saving ? 'A guardar…' : 'Usar este plano'}
          </button>
        </>
      ) : (
        <>
          <button type="button" className="lib-btn" onClick={onClose}>Cancelar</button>
          <button type="button" className="lib-btn primary" onClick={() => generate()} disabled={busy} data-testid="myplan-generate">
            {busy ? 'A gerar…' : 'Gerar proposta'}
          </button>
        </>
      )}
    >
      {errors.length > 0 && (
        <div className="lib-notice bad" role="alert" data-testid="myplan-errors">
          <ul>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      {!plan && (
        <div data-testid="myplan-params">
          <p className="lib-hint" style={{ marginTop: 0 }}>
            Propomos um <strong>plano semanal</strong> só com exercícios que este ginásio consegue fazer.
            Nada é gravado até carregar em «Usar este plano».
          </p>
          <div className="lib-field">
            <label className="lib-label" htmlFor="myplan-goal">Objetivo</label>
            <select id="myplan-goal" className="lib-select" value={form.goal} onChange={(e) => set({ goal: e.target.value })}>
              {GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="lib-field">
            <label className="lib-label" htmlFor="myplan-level">Experiência</label>
            <select id="myplan-level" className="lib-select" value={form.experience} onChange={(e) => set({ experience: e.target.value })}>
              {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="lib-field">
            <label className="lib-label" htmlFor="myplan-days">Dias por semana</label>
            <select id="myplan-days" className="lib-select" value={form.daysPerWeek} onChange={(e) => set({ daysPerWeek: Number(e.target.value) })}>
              {DAYS_PER_WEEK.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="lib-field">
            <label className="lib-label" htmlFor="myplan-minutes">Duração da sessão (min)</label>
            <input id="myplan-minutes" className="lib-input" type="number" min={30} max={120} step={5} value={form.sessionMinutes}
              onChange={(e) => set({ sessionMinutes: e.target.value === '' ? '' : Number(e.target.value) })} />
            <span className="lib-hint">Entre 30 e 120 minutos, aquecimento incluído.</span>
          </div>
        </div>
      )}

      {plan && (
        <div data-testid="myplan-preview">
          <div className="lib-banner" role="status">
            <strong>{plan.title}</strong>
            {plan.split ? <span>{plan.split}</span> : null}
            <span>{days.length} dias · {total} exercícios</span>
          </div>

          {plan.warnings?.length > 0 && (
            <div className="lib-notice warn" role="status" data-testid="myplan-warnings">
              <ul>{plan.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          {days.map((day, dayIndex) => (
            <section key={`${day.name}-${dayIndex}`} className="lib-section" style={{ marginBottom: 16 }} aria-label={day.name} data-testid={`myplan-day-${dayIndex}`}>
              <h3 style={{ margin: '0 0 2px', fontSize: '1.02rem' }}>{day.name}</h3>
              <div className="lib-hint">{day.focus ? `${day.focus} · ` : ''}~{day.estimatedMinutes} min</div>
              {(day.exercises || []).length === 0 ? <p className="lib-hint">Este dia ficou sem exercícios.</p> : (
                <ol className="gm-list" style={{ margin: 0, padding: 0 }}>
                  {(day.exercises || []).map((ex, exIndex) => (
                    <li key={`${ex.exerciseLibraryId}-${exIndex}`} className="gm-list-row">
                      <span className="gm-list-main">
                        <span className="gm-list-title gm-list-name">{ex.name}</span>
                        <span className="gm-list-sub">{prescriptionLine(ex) || 'Sem prescrição'}</span>
                        <span className="gm-list-sub">{ex.groupLabel}{ex.equipment ? ` · ${ex.equipment}` : ''}</span>
                        {(ex.fromHistory || ex.startingLoad) && ex.targetWeightKg != null && ex.progressionNote ? (
                          <span className="gm-list-sub" data-testid={`myplan-progression-${exIndex}`}>
                            {ex.progressionNote}{ex.progressed ? ' 📈' : ''}
                          </span>
                        ) : null}
                        {ex.reason ? <span className="gm-list-sub" data-testid={`myplan-reason-${exIndex}`}>Porquê: {ex.reason}</span> : null}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          ))}

          {plan.volume?.length > 0 && (
            <section aria-label="Volume semanal" data-testid="myplan-volume">
              <h3 style={{ fontSize: '1.02rem' }}>Séries por semana, por grupo</h3>
              <ul className="lib-hint" style={{ margin: 0, paddingLeft: 18 }}>
                {plan.volume.map((v) => <li key={v.groupCode}>{v.groupLabel}: {v.setsPerWeek} séries · {v.frequency}× por semana</li>)}
              </ul>
            </section>
          )}

          {plan.notes?.length > 0 && (
            <section aria-label="Notas do gerador" data-testid="myplan-notes">
              <h3 style={{ fontSize: '1.02rem' }}>Notas</h3>
              <ul style={{ margin: 0, paddingLeft: 20 }}>{plan.notes.map((n) => <li key={n}>{n}</li>)}</ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
